import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money, productImage } from "@/lib/api";
import { SectionTitle, MoneyStat, Thumb, SelectionBadge, ProcurementBadge } from "@/components/Bits";
import RoomBudgetBar from "@/components/RoomBudgetBar";

export default function BudgetView({ projectId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/projects/${projectId}/budget`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [projectId]);

  if (!data) return <div className="py-16 text-center text-sm text-fog">Loading budget…</div>;
  const t = data.totals;

  return (
    <div className="space-y-12" data-testid="budget-view">
      <div>
        <SectionTitle>Budget Summary</SectionTitle>
        <div className="card-surface grid gap-8 p-7 sm:grid-cols-2 lg:grid-cols-5">
          <MoneyStat testId="budget-overall" label="Overall Budget" value={t.budget_total} />
          <MoneyStat testId="budget-proposed" label="Proposed" value={t.proposed} sub="Excludes declined" />
          <MoneyStat testId="budget-approved" label="Approved" value={t.approved} tone="text-lime-dark" />
          <MoneyStat testId="budget-ordered" label="Ordered / Committed" value={t.ordered} />
          <MoneyStat testId="budget-remaining" label="Remaining" value={t.remaining} tone={t.remaining < 0 ? "text-clay" : "text-ink"} sub={`Actual delivered ${money(t.actual)}`} />
        </div>
        <div className="mt-5 grid gap-5 sm:grid-cols-4">
          {[["Design Fee", data.project.design_fee], ["Procurement Budget", data.project.procurement_budget], ["Installation", data.project.installation_budget], ["Contingency", data.project.contingency]].map(([l, v]) => (
            <div key={l} className="card-surface p-5">
              <div className="overline">{l}</div>
              <div className="num mt-2 text-lg text-ink">{money(v)}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <SectionTitle>Category Breakdown</SectionTitle>
        <div className="card-surface overflow-x-auto scroll-thin">
          <table className="w-full min-w-[720px] text-sm" data-testid="budget-by-category">
            <thead>
              <tr className="border-b border-line text-left">
                {["Category", "Items", "Proposed", "Approved", "Ordered", "Actual", "Designer Cost"].map((h) => (
                  <th key={h} className="overline px-5 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.by_category.map((c) => (
                <tr key={c.name} className="border-b border-line last:border-0 hover:bg-bone">
                  <td className="px-5 py-3 text-ink">{c.name}</td>
                  <td className="num px-5 py-3 text-right text-stone">{c.count}</td>
                  <td className="num px-5 py-3 text-right">{money(c.proposed)}</td>
                  <td className="num px-5 py-3 text-right text-lime-dark">{money(c.approved)}</td>
                  <td className="num px-5 py-3 text-right">{money(c.ordered)}</td>
                  <td className="num px-5 py-3 text-right">{money(c.actual)}</td>
                  <td className="num px-5 py-3 text-right text-stone">{money(c.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <SectionTitle>Room Breakdown</SectionTitle>
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3" data-testid="budget-by-room">
          {data.by_room.map((r) => (
            <div key={r.space_id || "unassigned"} className="card-surface p-6">
              <div className="flex items-baseline justify-between">
                {r.space_id ? (
                  <Link to={`/spaces/${r.space_id}`} className="font-serif text-lg text-ink hover:text-lime-dark">{r.name}</Link>
                ) : (
                  <span className="font-serif text-lg text-ink">{r.name}</span>
                )}
                <span className="num text-xs text-fog">{r.count} items</span>
              </div>
              <div className="mt-5 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-stone">Budget</span><span className="num">{money(r.budget)}</span></div>
                <div className="flex justify-between"><span className="text-stone">Approved</span><span className="num text-lime-dark">{money(r.approved)}</span></div>
                {r.pending > 0 && (
                  <div className="flex justify-between"><span className="text-stone">Awaiting approval</span><span className="num">{money(r.pending)}</span></div>
                )}
                <div className="flex justify-between"><span className="text-stone">Remaining</span><span className={`num ${r.remaining < 0 ? "text-clay" : ""}`}>{money(r.remaining)}</span></div>
              </div>
              <div className="mt-5">
                <RoomBudgetBar testId={`budget-room-bar-${r.space_id || "unassigned"}`} budget={r.budget} approved={r.approved} pending={r.pending} compact />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <SectionTitle>Product-Level Detail</SectionTitle>
        <div className="card-surface overflow-x-auto scroll-thin">
          <table className="w-full min-w-[900px] text-sm" data-testid="budget-line-items">
            <thead>
              <tr className="border-b border-line text-left">
                {["", "Product", "Room", "Category", "Qty", "Client Total", "Designer Cost", "Approval", "Order"].map((h, i) => (
                  <th key={i} className="overline px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.line_items.map((l) => (
                <tr key={l.id} className="border-b border-line last:border-0 hover:bg-bone">
                  <td className="px-4 py-3"><Link to={`/products/${l.id}`}><Thumb src={productImage(l)} className="h-10 w-12" /></Link></td>
                  <td className="px-4 py-3"><Link to={`/products/${l.id}`} className="text-ink hover:text-lime-dark">{l.name}</Link></td>
                  <td className="px-4 py-3 text-stone">{l.space_name || "—"}</td>
                  <td className="px-4 py-3 text-stone">{l.category}</td>
                  <td className="num px-4 py-3 text-right">{l.quantity}</td>
                  <td className="num px-4 py-3 text-right">{money(l.client_total)}</td>
                  <td className="num px-4 py-3 text-right text-stone">{money(l.designer_total_cost)}</td>
                  <td className="px-4 py-3"><SelectionBadge status={l.selection_status} /></td>
                  <td className="px-4 py-3"><ProcurementBadge status={l.procurement_status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
